// Logo.js
// Brutal Panda pixel logo, rendered as inline SVG so every fill can use the
// site's CSS variables and follow the light/dark toggle automatically.
// Static SVG copies for favicon / social use live in public/logo/.
import React from "react";

/* 16 x 15 pixel grid.
   W = face (--fg)  B = ears, eye patches, nose, mouth (--bg)
   G = eyes (--accent-light)  . = transparent */
const PANDA = [
  "..WW........W...",
  ".WBBW......WBBW.",
  ".WBBWWWWWWWWBBW.",
  ".WWWWWWWWWWWWWW.",
  "WWWWWWWWWWWWWWWW",
  "WWWWWWWWWWWWWWWW",
  "WWBBWWWWWWWWBBWW",
  "WWBBBWWWWWWBBBWW",
  "WWBGGBWWWWBGGBWW",
  "WWWBBWWWWWWBBWWW",
  "WWWWWWWBBWWWWWWW",
  "WWWWWWWBBWWWWWWW",
  ".WWWWWBBBBWWWWW.",
  "..WWWBWWWWBWWW..",
  "...WWWWWWWWWW...",
];

/* 5 x 7 pixel font, only the glyphs the wordmark needs */
const FONT = {
  A: [".###.", "#...#", "#...#", "#####", "#...#", "#...#", "#...#"],
  B: ["####.", "#...#", "#...#", "####.", "#...#", "#...#", "####."],
  D: ["####.", "#...#", "#...#", "#...#", "#...#", "#...#", "####."],
  L: ["#....", "#....", "#....", "#....", "#....", "#....", "#####"],
  N: ["#...#", "##..#", "#.#.#", "#..##", "#...#", "#...#", "#...#"],
  P: ["####.", "#...#", "#...#", "####.", "#....", "#....", "#...."],
  R: ["####.", "#...#", "#...#", "####.", "#.#..", "#..#.", "#...#"],
  T: ["#####", "..#..", "..#..", "..#..", "..#..", "..#..", "..#.."],
  U: ["#...#", "#...#", "#...#", "#...#", "#...#", "#...#", ".###."],
};

const FILL = {
  W: "var(--fg, #fff)",
  B: "var(--bg, #000)",
  G: "var(--accent-light, #8bac0f)",
};
const SHADOW = "var(--accent, #306230)";

/* Merge horizontal runs of the same cell into single <rect>s */
function rects(grid, x0, y0, colorOf, keyPrefix) {
  const out = [];
  grid.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const c = row[x];
      const fill = colorOf(c);
      if (!fill) {
        x++;
        continue;
      }
      let w = 1;
      while (x + w < row.length && row[x + w] === c) w++;
      out.push(
        <rect
          key={`${keyPrefix}-${x}-${y}`}
          x={x0 + x}
          y={y0 + y}
          width={w}
          height={1}
          fill={fill}
        />
      );
      x += w;
    }
  });
  return out;
}

function Mark({ x = 0, y = 0, shadow = true }) {
  return (
    <>
      {shadow && rects(PANDA, x + 1, y + 1, c => (c === "." ? null : SHADOW), "sh")}
      {rects(PANDA, x, y, c => FILL[c] || null, "px")}
    </>
  );
}

function Word({ text, x, y }) {
  const out = [];
  let cx = x;
  for (const ch of text) {
    if (ch === " ") {
      cx += 4;
      continue;
    }
    out.push(...rects(FONT[ch], cx, y, c => (c === "#" ? FILL.W : null), `t${cx}`));
    cx += 6;
  }
  return { nodes: out, end: cx - 1 };
}

/**
 * <Logo variant="mark" />     panda only               (17 x 16 units)
 * <Logo variant="oneline" />  panda + BRUTAL PANDA_    (one row, 16 units tall)
 * <Logo variant="stacked" />  panda + BRUTAL / PANDA_  (two rows)
 * shadow={false} drops the deep-green offset for a flatter, quieter mark.
 * Size it with CSS (width/height); the SVG scales on its viewBox.
 */
export default function Logo({ variant = "mark", title = "Brutal Panda", shadow = true, ...props }) {
  let width = 17;
  let height = 16;
  let body;

  if (variant === "oneline") {
    const word = Word({ text: "BRUTAL PANDA", x: 20, y: 4 });
    width = word.end + 5;
    body = (
      <>
        <Mark shadow={shadow} />
        {word.nodes}
        <rect x={word.end + 2} y={4} width={3} height={7} fill={FILL.G} />
      </>
    );
  } else if (variant === "stacked") {
    const w1 = Word({ text: "BRUTAL", x: 20, y: 0 });
    const w2 = Word({ text: "PANDA", x: 20, y: 8 });
    width = w1.end + 1;
    body = (
      <>
        <Mark shadow={shadow} />
        {w1.nodes}
        {w2.nodes}
        <rect x={w2.end + 2} y={8} width={3} height={7} fill={FILL.G} />
      </>
    );
  } else {
    body = <Mark shadow={shadow} />;
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${width} ${height}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={title}
      {...props}
    >
      <title>{title}</title>
      {body}
    </svg>
  );
}
